import { IStorefrontSettings, IStorefrontStyle, StorefrontStyleElementType } from '../../../models/Storefront';
import { getActiveStorefrontStyles, isStyleToken, resolveStyleToken } from './stylesConfig';

// Unified global style config, persisted as a JSON string on IStorefrontSettings.styleConfig.
// Styles are read JSON-first, falling back to the individual legacy fields on the settings object
// (so existing storefronts keep rendering). The admin writes ONLY to styleConfig.

export const STYLE_KEYS = [
    'menuBarColor',
    'menuBarTextColor',
    'backgroundColor',
    'textColor',
    'headerFont',
    'defaultFont',
    'sectionBackgroundColor',
    'sectionHeaderBackgroundColor',
    'sectionFooterBackgroundColor',
] as const;

export type StyleKey = typeof STYLE_KEYS[number];

// Storefront-wide loading spinner styling, stored as a nested object under styleConfig.spinner.
// Mirrors ac-storefront-web: Components/Helpers/globalStyleConfig.ts (ISpinnerConfig).
export interface ISpinnerConfig {
    color?: string;
    labelColor?: string;
    labelFont?: string;
    size?: string;
}

// The storefront's default Storefront Style per element type ({ section: styleId, card: styleId, … }).
// Purely an authoring-time setting: newly created sections seed their $styleRefs from it. Stored in
// styleConfig (per storefront, travels through preview/publish); the shopper never reads it.
export type IDefaultStyleMap = Partial<Record<StorefrontStyleElementType, string>>;

export type IStyleConfig = { [K in StyleKey]?: string } & { spinner?: ISpinnerConfig } & { defaultStyles?: IDefaultStyleMap } & { [prop: string]: string | ISpinnerConfig | IDefaultStyleMap | undefined };

export function parseStyleConfig(raw?: string): IStyleConfig {
    if (!raw) return {};
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as IStyleConfig) : {};
    } catch {
        return {};
    }
}

export function serializeStyleConfig(config: IStyleConfig): string {
    return JSON.stringify(config);
}

// Effective per-storefront value for a style key: styleConfig wins over the legacy field.
// A key present but empty ('') in styleConfig means "cleared/inherit" and intentionally
// shadows the legacy field (so clearing in the admin actually clears).
export function styleValue(settings: IStorefrontSettings | undefined, key: StyleKey): string | undefined {
    const cfg = parseStyleConfig(settings?.styleConfig);
    const v = cfg[key] as string | undefined;
    return v !== undefined ? v : (settings as unknown as Record<string, string | undefined>)?.[key];
}

/**
 * The PAINTING form of `styleValue`: the same styleConfig-first lookup, with any `$palette:` /
 * `$font:` token resolved against the currently-effective Storefront Styles (server list merged
 * with the Styles panel's unsaved draft, published into the registry by StorefrontStylesProvider).
 *
 * This is the pages editor's choke point for the shopper's `resolveSettingsTokens`: every preview
 * surface reads its menu-bar / background / text colours and its header/default fonts through
 * `styleValue`, so resolving here - and ONLY at the sites that paint - makes a tokenized settings
 * field preview exactly as the shopper will render it.
 *
 * Deliberately NOT folded into `styleValue` itself: the settings CONTROLS (the colour/font pickers
 * in the header and home editors, the classic settings forms) read the same helper and must keep
 * seeing the raw token, or the picker could not show the link state and every edit would silently
 * detach it.
 *
 * An unresolvable token yields undefined - the field reads as unset, exactly as the renderer does.
 */
export function resolvedStyleValue(settings: IStorefrontSettings | undefined, key: StyleKey, styles?: IStorefrontStyle[]): string | undefined {
    const raw = styleValue(settings, key);
    if (!isStyleToken(raw)) return raw;
    return resolveStyleToken(raw, styles ?? getActiveStorefrontStyles());
}

// Write a style patch to styleConfig only; the legacy fields are left untouched.
export function setStyle(settings: IStorefrontSettings, patch: Partial<Record<StyleKey, string | undefined>>): IStorefrontSettings {
    const next: IStyleConfig = { ...parseStyleConfig(settings.styleConfig), ...patch };
    return { ...settings, styleConfig: serializeStyleConfig(next) };
}

/** The storefront's default-style choices ({} when none set). */
export function getDefaultStyleIds(settings?: IStorefrontSettings): IDefaultStyleMap {
    const map = parseStyleConfig(settings?.styleConfig).defaultStyles;
    return map && typeof map === 'object' && !Array.isArray(map) ? map : {};
}

/** Set (or clear, with undefined) the default style for one element type. */
export function setDefaultStyleId(settings: IStorefrontSettings, elementType: StorefrontStyleElementType, styleId: string | undefined): IStorefrontSettings {
    const cfg = parseStyleConfig(settings.styleConfig);
    const map: IDefaultStyleMap = { ...getDefaultStyleIds(settings) };
    if (styleId) map[elementType] = styleId;
    else delete map[elementType];
    if (Object.keys(map).length > 0) cfg.defaultStyles = map;
    else delete cfg.defaultStyles;
    return { ...settings, styleConfig: serializeStyleConfig(cfg) };
}

// The storefront-wide spinner styling under styleConfig.spinner.
export function spinnerValue(settings: IStorefrontSettings | undefined): ISpinnerConfig {
    return parseStyleConfig(settings?.styleConfig).spinner ?? {};
}

// Merge a patch into styleConfig.spinner (undefined/'' values drop the key).
export function setSpinner(settings: IStorefrontSettings, patch: Partial<ISpinnerConfig>): IStorefrontSettings {
    const cfg = parseStyleConfig(settings.styleConfig);
    const merged: ISpinnerConfig = { ...(cfg.spinner ?? {}), ...patch };
    (Object.keys(merged) as (keyof ISpinnerConfig)[]).forEach(k => {
        if (merged[k] === undefined || merged[k] === '') delete merged[k];
    });
    const next: IStyleConfig = { ...cfg, spinner: merged };
    return { ...settings, styleConfig: serializeStyleConfig(next) };
}
